import React, { useEffect, useState } from 'react';
import { createCustomField, updateCustomField, CustomFieldEnum, ICustomField } from 'wisestep-api';
import { WsDialog, WsDialogSize } from 'wisestep-base';

import { WsCustomField } from '../../../components/BaseCustomField/WsCustomField';
import { useCustomFields } from '../../../features/clientListingPage/hooks/useCustomFields';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import {
  clientSettingsIndexOptions,
  hardcodedClientFields,
  fieldFormDefaultValues,
} from '../helper/ClientSettingsHelper';

import { ClientCustomFieldForm } from './ClientCustomFieldForm';

interface FieldFormData extends Omit<ICustomField, 'options'> {
  options: { value: string }[];
}

const CustomizeClientFields = () => {
  const { customFields, defaultFields, refresh } = useCustomFields(CustomFieldEnum.CLIENT);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<FieldFormData | undefined>(undefined);
  const [defaultFieldsList, setDefaultFieldsList] = useState<ICustomField[]>(hardcodedClientFields);

  useEffect(() => {
    if (!defaultFields) return;
    setDefaultFieldsList((prevList) =>
      prevList.map((item) => {
        const match = defaultFields.find((df) => df.name === item.name);
        return match ?? item;
      })
    );
  }, [defaultFields]);

  const handleEditField = (field?: ICustomField) => {
    const formData: FieldFormData | undefined = field
      ? {
          ...field,
          options: field.options?.map((value) => ({ value })) || [],
        }
      : undefined;
    setSelectedField(formData);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedField(undefined);
  };

  const handleSave = () => {
    handleCloseDialog();
  };

  const createField = async (field: ICustomField) => {
    await createCustomField(field);
    await refresh();
  };

  const updateField = async (field: ICustomField) => {
    await updateCustomField(field);
    await refresh();
  };

  return (
    <>
      {customFields && (
        <WsCustomField
          title="Customize Client Fields"
          entityType={CustomFieldEnum.CLIENT}
          breadCrumbList={getSettingBreadCrumbList(
            ISettingBreadCrumbTypes.CLIENTS,
            Pages['CUSTOMIZE_CLIENT_FIELDS']
          )}
          indexOptions={clientSettingsIndexOptions}
          allFields={[...defaultFieldsList, ...customFields]}
          createService={createField}
          updateService={updateField}
          onEditField={handleEditField}
        />
      )}
      <WsDialog openDialog={isDialogOpen} onClose={handleCloseDialog} size={WsDialogSize.MEDIUM}>
        <ClientCustomFieldForm
          defaultValues={selectedField ?? (fieldFormDefaultValues as FieldFormData)}
          entityType={CustomFieldEnum.CLIENT}
          title="Client Field"
          createService={createField}
          updateService={updateField}
          onSave={handleSave}
          onClose={handleCloseDialog}
        />
      </WsDialog>
    </>
  );
};

export default CustomizeClientFields;
